import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, SendHorizonal, TriangleAlert } from "lucide-react";

const STATUS_TONE = {
  ONLINE: "border-primary/40 text-primary bg-primary/10",
  approved: "border-primary/40 text-primary bg-primary/10",
};

const tone = (value) => STATUS_TONE[value] || "border-destructive/40 text-destructive bg-destructive/10";

/**
 * Live WhatsApp health for the clinic alerts: is Twilio's sender online, are the
 * alert templates approved by WhatsApp, and does a real test message land? Meta
 * blocks business-initiated messages without an approved template, so the panel
 * has to show that state instead of silently falling back to SMS.
 */
export const WhatsAppHealth = ({ onChange }) => {
  const [state, setState] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/whatsapp/status");
      setState(data);
    } catch (e) {
      setState({ error: formatApiError(e.response?.data?.detail), templates: [], blockers: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp/test", { to: to.trim() || null });
      toast.success(`WhatsApp delivered to ${data.to}`, { description: `Twilio status: ${data.status}` });
      onChange?.();
    } catch (e) {
      toast.error("WhatsApp test failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
      load();
    }
  };

  if (!state) return null;

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4 mb-6" data-testid="whatsapp-health">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="overline">WhatsApp alerts</span>
        {state.ready ? (
          <Badge variant="outline" className={tone("ONLINE")} data-testid="whatsapp-ready-badge">
            <CheckCircle2 size={12} className="mr-1" /> Live
          </Badge>
        ) : (
          <Badge variant="outline" className={tone("blocked")} data-testid="whatsapp-blocked-badge">
            <TriangleAlert size={12} className="mr-1" /> Not delivering yet
          </Badge>
        )}
        {state.sender && (
          <span className="text-xs text-muted-foreground" data-testid="whatsapp-sender">
            sender {state.sender} · {state.sender_status || "unknown"}
          </span>
        )}
      </div>

      <div className="grid gap-1.5 mb-3">
        {(state.templates || []).map((t) => (
          <div key={t.use} className="flex items-center gap-2 text-xs" data-testid={`whatsapp-template-${t.use.split(" ")[0]}`}>
            <Badge variant="outline" className={`${tone(t.status)} text-[10px] capitalize`}>{t.status}</Badge>
            <span className="text-muted-foreground">
              {t.use} template{t.name ? ` · ${t.name}` : ""}
              {t.rejection_reason ? ` — ${t.rejection_reason}` : ""}
            </span>
          </div>
        ))}
      </div>

      {(state.blockers || []).length > 0 && (
        <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1 mb-3" data-testid="whatsapp-blockers">
          {state.blockers.map((b) => <li key={b}>{b}</li>)}
        </ul>
      )}
      {(state.notices || []).length > 0 && (
        <ul className="text-xs text-muted-foreground/90 list-disc pl-4 space-y-1 mb-3" data-testid="whatsapp-notices">
          {state.notices.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
      {state.error && (
        <p className="text-xs text-destructive mb-3" data-testid="whatsapp-health-error">{state.error}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={state.destination || "+9198…"}
          className="bg-transparent w-44 h-9"
          data-testid="whatsapp-test-number"
        />
        <Button
          variant="outline"
          className="btn-pill h-9"
          disabled={busy}
          onClick={sendTest}
          data-testid="whatsapp-test-send"
        >
          {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <SendHorizonal size={13} className="mr-1.5" />}
          {busy ? "Sending…" : "Send test WhatsApp"}
        </Button>
        <span className="text-xs text-muted-foreground">
          {state.sms_fallback ? "SMS fallback is available." : "No SMS fallback — add a Twilio number to enable one."}
        </span>
      </div>
    </div>
  );
};

export default WhatsAppHealth;
